import i18n from '../i18n'

// Transport text must never reach the user: an axios message the backend
// relayed from Google ("Request failed with status code 403" toasted on every
// keystroke of the address field, owner feedback 2026-09-24), a Node error
// code, a stack fragment.
export const looksTechnical = (text: string) =>
  /status code \d{3}|\bE(CONN\w*|TIMEDOUT|NOTFOUND|HOSTUNREACH|PIPE)\b|socket hang up|^(Type|Range|Syntax)?Error\b|\.js:\d+/.test(
    text,
  )

// the Cyrillic block, U+0400..U+04FF
const CYRILLIC = new RegExp(`[${String.fromCharCode(0x400)}-${String.fromCharCode(0x4ff)}]`)

/**
 * Whether a server text can be shown as is: readable prose written in the
 * page's language. Some endpoints still answer in English (or Ukrainian)
 * whatever the page language is; such text gives way to the site's own
 * translation.
 */
export const isReadableServerText = (text: unknown): text is string => {
  if (typeof text !== 'string' || !text.trim() || looksTechnical(text)) return false
  const english = (i18n.resolvedLanguage || i18n.language) === 'en'
  return english ? !CYRILLIC.test(text) : CYRILLIC.test(text)
}

export const statusMessageKey = (status: unknown) => {
  const code = Number(status)
  if (code === 403) return 'errors.FORBIDDEN'
  if (code === 404) return 'errors.NOT_FOUND'
  if (code === 413) return 'errors.PAYLOAD_TOO_LARGE'
  if (code === 429) return 'errors.TOO_MANY_REQUESTS'
  if (code >= 500) return 'errors.SERVER'
  return 'errors.UNEXPECTED'
}

interface ApiFailure {
  status?: unknown
  data?: unknown
}

/**
 * The best text a failed request carries, or undefined. Rejected input comes
 * with the backend's exact reason in the user's language; otherwise the
 * site's translation of the error code wins, then a readable server text. A
 * code without a translation used to reach forms as the raw key
 * "errors.SOME_CODE".
 */
export const failureText = (failure: unknown): string | undefined => {
  const data = (failure as ApiFailure | null | undefined)?.data
  const body = typeof data === 'object' && data !== null ? (data as Record<string, unknown>) : {}
  const code = typeof body.code === 'string' ? body.code : undefined
  const message = [body.message, body.error].find(isReadableServerText)

  if (code === 'VALIDATION_ERROR' && message) return message
  if (code && i18n.exists(`errors.${code}`)) return String(i18n.t(`errors.${code}`))
  return message
}

/** The text for a failed request, falling back to a key (by default one picked by HTTP status). */
export const errorText = (failure: unknown, fallbackKey?: string): string =>
  failureText(failure) ??
  String(
    i18n.t(fallbackKey ?? statusMessageKey((failure as ApiFailure | null | undefined)?.status)),
  )

/** A confirmation: the server's text when it is readable, else the site's own. */
export const successText = (response: unknown, fallbackKey: string): string => {
  const message = (response as { message?: unknown } | null | undefined)?.message
  return isReadableServerText(message) ? message : String(i18n.t(fallbackKey))
}
